import { useEffect, useState, useCallback } from "react";
import { api, num } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { GlassCard } from "../components/GlassCard";
import { Plus, TrendingUp, TrendingDown, ArrowUpRight, Wallet, Download, X, ShieldAlert } from "lucide-react";
import { Link } from "react-router-dom";
import CreateAccountDialog from "../components/CreateAccountDialog";
import CreateEntryDialog from "../components/CreateEntryDialog";
import ShortcutsPanel from "../components/ShortcutsPanel";
import Sparkline from "../components/Sparkline";

export default function Dashboard() {
  const { activeWorkspace } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [entries, setEntries] = useState([]);
  const [level, setLevel] = useState("owner");
  const [busy, setBusy] = useState(true);
  const [openAccount, setOpenAccount] = useState(false);
  const [openEntry, setOpenEntry] = useState(false);
  const [sparklines, setSparklines] = useState({}); // { [accountId]: {series, name, balance} }
  const [backupBanner, setBackupBanner] = useState(null); // {days_since, last_manual_backup_at} | null
  const [downloadingBackup, setDownloadingBackup] = useState(false);

  const refresh = useCallback(async () => {
    setBusy(true);
    try {
      const [a, e, s] = await Promise.all([
        api.get("/accounts"),
        api.get("/entries", { params: { status: "active" } }),
        api.get("/accounts/sparklines", { params: { days: 30, limit: 12 } }).catch(() => ({ data: { accounts: [] } })),
      ]);
      setAccounts((a.data.accounts || []).filter((x) => x.active !== false));
      setLevel(a.data.workspace_level || "owner");
      setEntries(e.data.entries || []);
      const map = {};
      for (const acc of s.data.accounts || []) {
        map[acc.id] = acc;
      }
      setSparklines(map);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const h = () => refresh();
    window.addEventListener("workspace-changed", h);
    return () => window.removeEventListener("workspace-changed", h);
  }, [refresh]);

  // Backup reminder — check on first render whether it's been >= 7 days since
  // the user's last manual ZIP download. Only owners see it (backend enforces
  // owner-only download anyway). "dismissed" is per-session so a user who
  // waves it off doesn't get nagged again the same day.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (level !== "owner") return;
      if (sessionStorage.getItem("backup-banner-dismissed") === "1") return;
      try {
        const { data } = await api.get("/backup/reminder-status");
        if (!cancelled && data?.needs_reminder) setBackupBanner(data);
      } catch {
        /* silent — banner is a nudge, not critical */
      }
    })();
    return () => { cancelled = true; };
  }, [level]);

  const downloadBackupZip = async () => {
    setDownloadingBackup(true);
    try {
      const resp = await api.get("/backup/all-ledgers.zip", { responseType: "blob" });
      const cd = resp.headers["content-disposition"] || resp.headers["Content-Disposition"] || "";
      const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
      const filename = m ? decodeURIComponent(m[1]) : `solartoner-backup-${new Date().toISOString().slice(0, 10)}.zip`;
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      setBackupBanner(null); // downloaded → hide banner
      sessionStorage.removeItem("backup-banner-dismissed");
    } catch (err) {
      alert(`Backup download failed: ${err?.response?.statusText || err.message}`);
    } finally {
      setDownloadingBackup(false);
    }
  };

  const dismissBackupBanner = () => {
    sessionStorage.setItem("backup-banner-dismissed", "1");
    setBackupBanner(null);
  };

  const totalPos = accounts.filter((a) => a.balance > 0).reduce((s, a) => s + a.balance, 0);
  const totalNeg = accounts.filter((a) => a.balance < 0).reduce((s, a) => s + a.balance, 0);
  const nameById = Object.fromEntries(accounts.map((a) => [a.id, a.name]));

  const canEdit = level !== "view";

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Hero */}
      <div className="space-y-2">
        <p className="text-stone-500">Welcome back{activeWorkspace ? "," : ""}</p>
        <h1 className="font-serif text-5xl sm:text-6xl leading-tight">
          {activeWorkspace?.name || "Your ledger"}
          {level !== "owner" && (
            <span className="ml-3 align-middle text-sm bg-white/70 rounded-full px-3 py-1 font-sans capitalize text-stone-600">
              {level} access
            </span>
          )}
        </h1>
        <p className="text-stone-500 max-w-xl">
          A private, immutable ledger. Every entry moves value between two accounts. Nothing is ever
          deleted.
        </p>
      </div>

      {/* Weekly backup reminder — owner-only, dismissible per session, resets
          automatically when the user actually downloads the ZIP. */}
      {backupBanner && (
        <div
          className="rounded-2xl border border-[color:var(--gold)]/40 bg-[color:var(--gold-soft)]/60 backdrop-blur-md px-5 py-4 flex items-start gap-4"
          data-testid="backup-reminder-banner"
        >
          <ShieldAlert size={20} className="text-[color:var(--gold-deep)] mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-serif text-lg text-[color:var(--gold-deep)]">
              Time for your weekly backup
            </div>
            <p className="text-sm text-stone-600 dark:text-stone-300 mt-0.5">
              {backupBanner.last_manual_backup_at
                ? `It's been ${backupBanner.days_since} days since your last manual backup.`
                : "You haven\u2019t downloaded a manual backup yet."}{" "}
              Download every account&apos;s ledger as a single ZIP so you always have a local copy.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={downloadBackupZip}
              disabled={downloadingBackup}
              className="btn-gold h-9 px-4 text-sm flex items-center gap-2 disabled:opacity-60"
              data-testid="backup-banner-download-btn"
            >
              <Download size={14} />
              {downloadingBackup ? "Preparing…" : "Download now"}
            </button>
            <button
              onClick={dismissBackupBanner}
              className="btn-ghost btn-ghost-sm flex items-center gap-1"
              data-testid="backup-banner-dismiss-btn"
              title="Remind me next login"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid md:grid-cols-3 gap-6">
        <GlassCard>
          <div className="flex items-start justify-between">
            <div>
              <div className="label-tiny">Positive Balance</div>
              <div className="font-serif text-4xl mt-2 text-emerald-700">{num(totalPos)}</div>
              <div className="text-xs text-stone-500 mt-1">
                {accounts.filter((a) => a.balance > 0).length} accounts
              </div>
            </div>
            <div className="w-10 h-10 rounded-2xl bg-emerald-50 flex items-center justify-center">
              <TrendingUp size={18} className="text-emerald-600" strokeWidth={1.5} />
            </div>
          </div>
        </GlassCard>

        <GlassCard>
          <div className="flex items-start justify-between">
            <div>
              <div className="label-tiny">Negative Balance</div>
              <div className="font-serif text-4xl mt-2 text-rose-700">{num(totalNeg)}</div>
              <div className="text-xs text-stone-500 mt-1">
                {accounts.filter((a) => a.balance < 0).length} accounts
              </div>
            </div>
            <div className="w-10 h-10 rounded-2xl bg-rose-50 flex items-center justify-center">
              <TrendingDown size={18} className="text-rose-600" strokeWidth={1.5} />
            </div>
          </div>
        </GlassCard>

        <GlassCard>
          <div className="flex items-start justify-between">
            <div>
              <div className="label-tiny">Active Entries</div>
              <div className="font-serif text-4xl mt-2">{entries.length}</div>
              <div className="text-xs text-stone-500 mt-1">Modifiable until finalized</div>
            </div>
            <div className="w-10 h-10 rounded-2xl glass-gold flex items-center justify-center">
              <Wallet size={18} className="text-[color:var(--gold-deep)]" strokeWidth={1.5} />
            </div>
          </div>
        </GlassCard>
      </div>

      {/* Actions */}
      {canEdit && (
        <div className="flex flex-wrap gap-3">
          <button
            className="btn-gold flex items-center gap-2"
            onClick={() => setOpenEntry(true)}
            data-testid="create-entry-btn"
          >
            <Plus size={16} strokeWidth={2} />
            New entry
          </button>
          {level === "owner" && (
            <button
              className="btn-ghost flex items-center gap-2"
              onClick={() => setOpenAccount(true)}
              data-testid="create-account-btn"
            >
              <Plus size={16} strokeWidth={2} />
              New account
            </button>
          )}
        </div>
      )}

      {/* Accounts grid */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-3xl">Accounts</h2>
          <span className="text-sm text-stone-500">{accounts.length} total</span>
        </div>
        {accounts.length === 0 ? (
          <GlassCard className="text-center py-16">
            <p className="text-stone-500">
              No accounts yet.{" "}
              {level === "owner" && (
                <button
                  onClick={() => setOpenAccount(true)}
                  className="text-[color:var(--gold-deep)] font-medium"
                >
                  Create your first
                </button>
              )}
            </p>
          </GlassCard>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {accounts.map((a) => {
              const spark = sparklines[a.id];
              return (
              <div
                key={a.id}
                className="glass rounded-2xl p-5 hover:-translate-y-0.5 transition-transform"
                data-testid={`account-card-${a.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-medium truncate pr-2">{a.name}</div>
                  <div
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      a.balance > 0
                        ? "bg-emerald-50 text-emerald-700"
                        : a.balance < 0
                          ? "bg-rose-50 text-rose-700"
                          : "bg-stone-100 text-stone-500"
                    }`}
                  >
                    {a.balance > 0 ? "Credit" : a.balance < 0 ? "Debit" : "Zero"}
                  </div>
                </div>
                <div
                  className={`font-serif text-3xl mt-3 ${
                    a.balance > 0
                      ? "text-emerald-700"
                      : a.balance < 0
                        ? "text-rose-700"
                        : "text-stone-600"
                  }`}
                >
                  {num(a.balance)}
                </div>
                <div className="flex items-end justify-between mt-1 gap-3">
                  <div className="text-xs text-stone-500">
                    Opening {num(a.opening_balance)}
                  </div>
                  {spark && spark.series && spark.series.length >= 2 && (
                    <div
                      title="30-day balance trend"
                      data-testid={`sparkline-${a.id}`}
                      className="flex-shrink-0"
                    >
                      <Sparkline
                        series={spark.series}
                        width={110}
                        height={30}
                        accent={a.balance > 0 ? "emerald" : a.balance < 0 ? "rose" : "stone"}
                      />
                    </div>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent entries */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-3xl">Recent entries</h2>
          <Link
            to="/entries"
            className="text-sm text-[color:var(--gold-deep)] flex items-center gap-1"
          >
            View all <ArrowUpRight size={14} />
          </Link>
        </div>
        {entries.length === 0 ? (
          <GlassCard className="text-center py-10 text-stone-500">
            No active entries. All caught up.
          </GlassCard>
        ) : (
          <GlassCard className="p-0 overflow-hidden">
            <div className="divide-y divide-stone-200/50">
              {entries.slice(0, 6).map((e) => (
                <div
                  key={e.id}
                  className="px-6 py-4 flex items-center justify-between hover:bg-white/40 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {nameById[e.from_account_id] || "?"}
                      <span className="mx-2 text-stone-400">→</span>
                      {nameById[e.to_account_id] || "?"}
                    </div>
                    {e.note && (
                      <div className="text-xs text-stone-500 truncate mt-0.5">{e.note}</div>
                    )}
                  </div>
                  <div
                    className={`font-serif text-xl ml-4 ${
                      e.amount >= 0 ? "text-emerald-700" : "text-rose-700"
                    }`}
                  >
                    {e.amount >= 0 ? "+" : ""}
                    {num(e.amount)}
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>
        )}
      </div>

      {/* Shortcuts panel */}
      <ShortcutsPanel />

      {openAccount && (
        <CreateAccountDialog
          open={openAccount}
          onClose={() => setOpenAccount(false)}
          onSaved={refresh}
        />
      )}
      {openEntry && (
        <CreateEntryDialog
          open={openEntry}
          onClose={() => setOpenEntry(false)}
          onSaved={refresh}
          accounts={accounts}
        />
      )}

      {busy && accounts.length === 0 && (
        <div className="text-center text-stone-400 text-sm">Loading…</div>
      )}
    </div>
  );
}
